/**
 * TISA COINS GAMIFICATION SYSTEM - CONFIGURATION
 *
 * All static reference data for the TISA Coins app.
 * Guilds, badges, bank accounts, level perks, and teacher overrides.
 *
 * This file is the source of truth for:
 * - Guild definitions and values (loaded from data/guilds.json)
 * - Badge catalog and tier progression (loaded from data/badges.json)
 * - Bank account types and interest rates
 * - Level-based unlocks and perks
 * - Teacher super-access permissions
 */

import guildsData from './data/guilds.json'
import badgesData from './data/badges.json'

// ============================================================================
// TYPES
// ============================================================================

export interface Guild {
  id: string;
  name: string;
  icon: string;
  color: string;
  motto: string;
  chant: string;
  values: string[];
  traits: string[];
  perks: string[];
  description: string;
}

export interface BadgeTier {
  xp: number;
  description: string;
}

export interface Badge {
  id: string;
  name: string;
  icon: string;
  category: 'academic' | 'social' | 'creative' | 'leadership' | 'character' | 'service' | 'excellence' | 'consistency' | 'innovation';
  description: string;
  tiers: {
    bronze: BadgeTier;
    silver: BadgeTier;
    gold: BadgeTier;
    platinum: BadgeTier;
    legendary: BadgeTier;
  };
}

export interface BankAccount {
  type: string;
  rate: number;
  name: string;
  locked: boolean;
  description: string;
  lockPeriod?: string;
}

export interface TeacherOverride {
  email: string;
  superAccess: boolean;
}

// ============================================================================
// GUILDS (8 Total - loaded from guilds.json)
// ============================================================================

export const GUILDS: Guild[] = guildsData as Guild[];

// ============================================================================
// BADGES (9 Total - loaded from badges.json)
// ============================================================================

export const BADGES: Badge[] = badgesData as Badge[];

// ============================================================================
// BANK ACCOUNTS
// ============================================================================

export const BANK_ACCOUNTS: BankAccount[] = [
  {
    type: 'vault',
    rate: 0,
    name: 'Vault',
    locked: false,
    description: 'Instant access to your coins. No interest, but total freedom.',
    lockPeriod: undefined
  },
  {
    type: 'savings_10',
    rate: 0.10,
    name: 'Safe Vault',
    locked: true,
    description: '10% interest - Your coins are locked until the end of the month.',
    lockPeriod: '1 month'
  },
  {
    type: 'savings_40',
    rate: 0.40,
    name: 'Growth Vault',
    locked: true,
    description: '40% interest - Locked for 3 months. Plan ahead!',
    lockPeriod: '3 months'
  },
  {
    type: 'savings_100',
    rate: 1.0,
    name: 'Risk Vault',
    locked: true,
    description: '100% interest (doubles your coins!) - Locked for 6 months.',
    lockPeriod: '6 months'
  },
  {
    type: 'savings_200',
    rate: 2.0,
    name: 'Legend Vault',
    locked: true,
    description: '200% interest (triples your coins!) - Locked for 1 full year. The ultimate commitment.',
    lockPeriod: '1 year'
  }
];

// ============================================================================
// CLASSES
// ============================================================================

export const CLASSES = [
  'Grade 6',
  'Grade 7',
  'Grade 8',
  'Grade 9'
];

// ============================================================================
// LEVEL SYSTEM & PERKS
// ============================================================================

/**
 * Calculate level from total XP
 * Formula: Level = floor(sqrt(XP / 50)) + 1
 *
 * Level 1: 0-49 XP
 * Level 2: 50-199 XP
 * Level 3: 200-449 XP
 * Level 4: 450-799 XP
 * etc.
 */
export const XP_TO_LEVEL = (xp: number): number => {
  return Math.floor(Math.sqrt(xp / 50)) + 1;
};

/**
 * Calculate XP needed for a specific level
 */
export const LEVEL_TO_XP = (level: number): number => {
  return Math.pow(level - 1, 2) * 50;
};

/**
 * Level-based unlocks and perks
 * Students can see what they'll unlock next
 */
export const LEVEL_PERKS: Record<number, string[]> = {
  1: ['Quest Access', 'Basic Bank Vault', 'Bronze Badge Path'],
  2: ['+5 XP on Reflections', 'Mystery Card Access', 'Guild Quest Board'],
  3: ['Mystery Shop Access', 'Silver Badge Path', 'Auction Preview'],
  4: ['Auction Access', 'Gold Badge Path', 'Market Vendor Role'],
  5: ['Prestige Title', 'Mentor Power', 'Guild Leadership', 'Platinum Badge Path'],
  6: ['Epic Quest Access', 'Double Interest Week (1x/year)', 'Advanced Privileges'],
  7: ['Create Market Listings', 'Legendary Badge Path', 'Guild Champion Status'],
  8: ['Ceremony Speaker', 'Special Event Access', 'VIP Auction Preview'],
  9: ['School Legend', 'Create Custom Quest (1x/month)', 'Mentor Younger Students'],
  10: ['TISA Hall of Fame', 'Lifetime Achievement Badge', 'Legacy Project Access']
};

/**
 * Get next level information
 */
export const getNextLevelPerks = (currentXP: number) => {
  const currentLevel = XP_TO_LEVEL(currentXP);
  const nextLevel = currentLevel + 1;
  const nextLevelXP = LEVEL_TO_XP(nextLevel);
  const perks = LEVEL_PERKS[nextLevel] || [];
  const progress = (currentXP - LEVEL_TO_XP(currentLevel)) / (nextLevelXP - LEVEL_TO_XP(currentLevel));

  return {
    currentLevel,
    nextLevel,
    currentLevelXP: LEVEL_TO_XP(currentLevel),
    nextLevelXP,
    xpToNextLevel: nextLevelXP - currentXP,
    perks,
    progress: Math.min(progress, 1) // Cap at 100%
  };
};

// ============================================================================
// TEACHER OVERRIDES
// ============================================================================

/**
 * Teachers with super access can:
 * - View all classes (not just their own)
 * - Access audit logs
 * - Issue XP refunds
 * - Perform emergency fixes
 *
 * Most teachers will NOT have super access - keeps the system fair and flat.
 */
export const TEACHER_OVERRIDES: TeacherOverride[] = [
  { email: 'artem@tisa.nl', superAccess: true },
  // Add other super-access teachers here as needed
];

/**
 * Check if a teacher has super access
 */
export const hasSuperAccess = (teacherEmail: string): boolean => {
  return TEACHER_OVERRIDES.some(t => t.email === teacherEmail && t.superAccess);
};

// ============================================================================
// SHOP CATALOG (Sample items - extend as needed)
// ============================================================================

export interface ShopItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category: 'supplies' | 'privileges' | 'events' | 'customization' | 'consumables';
  icon: string;
  availability?: 'always' | 'limited' | 'weekly' | 'monthly' | 'mystery' | 'quarterly';  // When item is available for purchase
  stock?: number;  // undefined = unlimited
  requiredLevel?: number;
  rarity?: 'common' | 'rare' | 'epic' | 'legendary';  // Item rarity for visual effects
  useEffect?: 'xp_boost' | 'skip_homework' | 'class_rule_override';  // Effect when consumed (for consumables)
  timesUsable?: number;  // How many times item can be used (1 = one-time, >1 = multi-use, undefined = permanent)
}

export const SHOP_ITEMS: ShopItem[] = [
  // ============================================================================
  // SCHOOL SUPPLIES (15 items)
  // ============================================================================
  { id: 'gel-pen-set', name: 'Gel Pen Set', description: '5 colorful glitter pens', price: 80, category: 'supplies', icon: '🖊️', availability: 'always', rarity: 'common' },
  { id: 'secret-notebook', name: 'Secret Notebook', description: 'Black leather-like notebook for secret plans', price: 120, category: 'supplies', icon: '📓', availability: 'limited', rarity: 'rare' },
  { id: '3d-ruler', name: '3D Ruler', description: 'Geometric folding ruler', price: 70, category: 'supplies', icon: '📏', availability: 'always', rarity: 'common' },
  { id: 'tisa-pencil-pack', name: 'TISA Pencil Pack', description: '5 branded pencils with guild logo toppers', price: 60, category: 'supplies', icon: '✏️', availability: 'always', rarity: 'common' },
  { id: 'magical-eraser', name: 'Magical Eraser', description: 'Glows slightly when used', price: 40, category: 'supplies', icon: '🧹', availability: 'mystery', rarity: 'rare' },
  { id: 'guild-planner-sticker-set', name: 'Guild Planner Sticker Set', description: 'Guild-themed weekly planner decor', price: 50, category: 'supplies', icon: '🎨', availability: 'always', rarity: 'common' },
  { id: 'sharpie-pack', name: 'Sharpie Pack', description: 'Bold colors for posters and quests', price: 100, category: 'supplies', icon: '🖍️', availability: 'limited', rarity: 'common' },
  { id: 'personalized-folder', name: 'Personalized Folder', description: 'With name + badge icon', price: 150, category: 'supplies', icon: '📁', availability: 'limited', rarity: 'epic' },
  { id: 'scratch-paper-art-pad', name: 'Scratch Paper Art Pad', description: 'Reveal colors underneath with a wooden stick', price: 90, category: 'supplies', icon: '🎨', availability: 'always', rarity: 'common' },
  { id: 'bookmark-kit', name: 'Bookmark Kit', description: 'Create your own tassel & charm bookmark', price: 60, category: 'supplies', icon: '🔖', availability: 'always', rarity: 'common' },
  { id: 'science-goggles-mini', name: 'Science Goggles (Mini)', description: 'Kid-safe for classroom experiments', price: 100, category: 'supplies', icon: '🥽', availability: 'limited', rarity: 'rare' },
  { id: 'role-id-lanyard', name: 'Role ID Lanyard', description: 'Colored lanyard + card holder with your role title', price: 110, category: 'supplies', icon: '🪪', availability: 'always', rarity: 'common' },
  { id: 'glow-in-dark-pencil', name: 'Glow-in-the-Dark Pencil', description: 'Light writing magic!', price: 70, category: 'supplies', icon: '✨', availability: 'mystery', rarity: 'epic' },
  { id: 'turn-in-tracker', name: '"Turn In Tracker" Sheet', description: 'Stamp-based homework tracker', price: 30, category: 'supplies', icon: '📋', availability: 'always', rarity: 'common' },
  { id: 'guild-banner-starter', name: 'Guild Banner Starter Pack', description: 'Small flag + marker set for guild flags', price: 140, category: 'supplies', icon: '🚩', availability: 'always', rarity: 'rare' },

  // ============================================================================
  // PRIVILEGES (15 items)
  // ============================================================================
  { id: 'sit-with-any-classmate', name: 'Sit with Any Classmate', description: 'For 1 day', price: 100, category: 'privileges', icon: '🪑', availability: 'always', rarity: 'common' },
  { id: 'music-selector', name: 'Music Selector', description: 'Choose class music during work time (1 period)', price: 100, category: 'privileges', icon: '🎵', availability: 'always', rarity: 'common' },
  { id: 'mystery-box-pull', name: 'Mystery Box Pull', description: '1 card from Mystery Box', price: 150, category: 'privileges', icon: '🎁', availability: 'weekly', rarity: 'rare' },
  { id: 'homework-skip', name: 'Homework Skip', description: 'Skip 1 assignment with teacher approval', price: 250, category: 'privileges', icon: '🎫', availability: 'monthly', rarity: 'epic', useEffect: 'skip_homework', timesUsable: 1 },
  { id: 'line-leader', name: 'Line Leader', description: 'Lead the line for a day', price: 80, category: 'privileges', icon: '👑', availability: 'always', rarity: 'common' },
  { id: 'board-artist', name: 'Board Artist', description: 'Design the morning board (approved)', price: 120, category: 'privileges', icon: '🖌️', availability: 'always', rarity: 'common' },
  { id: 'extra-break-pass', name: 'Extra Break Pass', description: '+5 min break (once per day max)', price: 150, category: 'privileges', icon: '⏰', availability: 'always', rarity: 'rare', useEffect: 'class_rule_override', timesUsable: 1 },
  { id: 'dress-like-teacher-day', name: 'Dress Like a Teacher Day', description: 'Role reversal outfit fun', price: 200, category: 'privileges', icon: '👔', availability: 'monthly', rarity: 'rare' },
  { id: 'choose-class-activity', name: 'Choose Class Activity', description: 'Decide a fun activity for the class (vote-based)', price: 300, category: 'privileges', icon: '🎯', availability: 'limited', rarity: 'epic' },
  { id: 'lunch-with-teacher', name: 'Lunch with a Teacher', description: 'Eat in teacher lounge (w/ approval)', price: 250, category: 'privileges', icon: '🍽️', availability: 'monthly', rarity: 'epic' },
  { id: 'pick-first-quest', name: 'Pick the First Quest', description: 'Choose which daily quest gets priority', price: 130, category: 'privileges', icon: '🎲', availability: 'always', rarity: 'common' },
  { id: 'skip-morning-duty', name: 'Skip a Morning Duty', description: 'Opt out of 1 morning task rotation', price: 140, category: 'privileges', icon: '🌅', availability: 'always', rarity: 'common' },
  { id: 'double-xp-token', name: 'Double XP Token', description: 'Doubles next quest XP (once per term)', price: 200, category: 'privileges', icon: '⚡', availability: 'limited', rarity: 'legendary', useEffect: 'xp_boost', timesUsable: 1 },
  { id: 'insta-auction-access', name: 'Insta-Auction Access', description: 'Pre-bid view and item reservation', price: 300, category: 'privileges', icon: '🔨', availability: 'mystery', rarity: 'legendary' },
  { id: 'announce-something-funny', name: 'Announce Something Funny', description: 'Share a class-approved joke at morning meeting', price: 60, category: 'privileges', icon: '😄', availability: 'always', rarity: 'common' },

  // ============================================================================
  // EVENTS (10 items)
  // ============================================================================
  { id: 'mini-guild-party', name: 'Mini Guild Party', description: 'Buy a snack & game for your whole guild', price: 500, category: 'events', icon: '🎉', availability: 'monthly', rarity: 'epic' },
  { id: 'dj-for-a-day', name: 'DJ for a Day', description: 'Choose school playlist for transitions', price: 400, category: 'events', icon: '🎧', availability: 'monthly', rarity: 'epic' },
  { id: 'pajama-day-pass', name: 'Pajama Day Pass', description: 'Wear pajamas to school (guild-wide event)', price: 250, category: 'events', icon: '🛏️', availability: 'monthly', rarity: 'rare' },
  { id: 'mini-cinema-ticket', name: 'Mini Cinema Ticket', description: 'Watch a short film with friends during break', price: 350, category: 'events', icon: '🎬', availability: 'weekly', rarity: 'rare' },
  { id: 'vip-ceremony-role', name: 'VIP Ceremony Role', description: 'Host or co-host the Friday ceremony', price: 300, category: 'events', icon: '🎤', availability: 'weekly', rarity: 'epic' },
  { id: 'guild-game-challenge', name: 'Guild Game Challenge', description: 'Sponsor a guild-vs-guild event', price: 500, category: 'events', icon: '⚔️', availability: 'monthly', rarity: 'epic' },
  { id: 'directors-tea-party', name: "Director's Tea Party", description: 'Tea & cookies with the Director (invite-only)', price: 700, category: 'events', icon: '☕', availability: 'quarterly', rarity: 'legendary' },
  { id: 'floor-is-lava-game', name: 'Floor is Lava Game', description: 'Organize this game for class/guild', price: 200, category: 'events', icon: '🌋', availability: 'always', rarity: 'common' },
  { id: 'bubble-bash', name: 'Bubble Bash', description: 'Blow giant bubbles outside during break', price: 200, category: 'events', icon: '🫧', availability: 'always', rarity: 'common' },
  { id: 'lucky-dice-roll', name: 'Lucky Dice Roll', description: 'Win prizes on roll (2–10 range)', price: 150, category: 'events', icon: '🎲', availability: 'mystery', rarity: 'rare' },

  // ============================================================================
  // CUSTOMIZATION (15 items)
  // ============================================================================
  { id: 'desk-customization-kit', name: 'Desk Customization Kit', description: 'Stickers, nameplate, borders', price: 200, category: 'customization', icon: '🖼️', availability: 'always', rarity: 'rare' },
  { id: 'role-enhancement-card', name: 'Role Enhancement Card', description: 'Visually upgrade your Role Card with new art', price: 300, category: 'customization', icon: '🃏', availability: 'monthly', rarity: 'epic' },
  { id: 'signature-color-tag', name: 'Signature Color Tag', description: 'Wear your chosen color band + notebook label', price: 100, category: 'customization', icon: '🏷️', availability: 'always', rarity: 'common' },
  { id: 'custom-guild-badge', name: 'Custom Guild Badge', description: '3D sticker for backpack or folder', price: 200, category: 'customization', icon: '🛡️', availability: 'limited', rarity: 'epic' },
  { id: 'bag-tag', name: 'Bag Tag', description: 'Personalized backpack tag with crest', price: 150, category: 'customization', icon: '🎒', availability: 'always', rarity: 'common' },
  { id: 'journal-cover-art', name: 'Journal Cover Art', description: 'Laminated custom art cover for school journal', price: 120, category: 'customization', icon: '📔', availability: 'always', rarity: 'common' },
  { id: 'design-a-tshirt-token', name: 'Design-a-T-Shirt Token', description: 'Submit your design to be printed for school wear', price: 600, category: 'customization', icon: '👕', availability: 'limited', rarity: 'legendary' },
  { id: 'character-avatar-sticker', name: 'Character Avatar Sticker', description: 'Create your RPG-style student avatar', price: 100, category: 'customization', icon: '🧙', availability: 'always', rarity: 'common' },
  { id: 'title-banner', name: 'Title Banner', description: 'Add "The Wise", "The Bold", etc. next to your name', price: 180, category: 'customization', icon: '👑', availability: 'always', rarity: 'rare' },
  { id: 'locker-decor-pack', name: 'Locker Decor Pack', description: 'Stickers + border strips for your locker', price: 120, category: 'customization', icon: '🗄️', availability: 'always', rarity: 'common' },
  { id: 'enchanted-bookmark', name: 'Enchanted Bookmark', description: 'With metal charm and glow ribbon', price: 90, category: 'customization', icon: '✨', availability: 'mystery', rarity: 'rare' },
  { id: 'guild-totem', name: 'Guild Totem', description: "Draw/print item that represents your guild's spirit", price: 200, category: 'customization', icon: '🗿', availability: 'always', rarity: 'epic' },
  { id: 'fantasy-role-id-frame', name: 'Fantasy Role ID Frame', description: 'Add fantasy border to your Role Card', price: 150, category: 'customization', icon: '🖼️', availability: 'always', rarity: 'rare' },
  { id: 'glow-pen-personalizer', name: 'Glow Pen Personalizer', description: 'Add glowing initials to one pen', price: 120, category: 'customization', icon: '✍️', availability: 'limited', rarity: 'rare' },
  { id: 'mini-guild-flag', name: 'Mini Guild Flag', description: 'Fabric flag to place on desk or carry to events', price: 180, category: 'customization', icon: '🚩', availability: 'always', rarity: 'common' },

  // ============================================================================
  // CONSUMABLES (10 items)
  // ============================================================================
  { id: 'guild-snack-token', name: 'Guild Snack Token', description: 'Healthy snack from guild jar', price: 60, category: 'consumables', icon: '🍎', availability: 'always', rarity: 'common' },
  { id: 'magic-tea-bag', name: 'Magic Tea Bag', description: 'Tea break with a mystery flavor', price: 70, category: 'consumables', icon: '🍵', availability: 'always', rarity: 'common' },
  { id: 'candy-scroll', name: 'Candy Scroll', description: 'Candy wrapped in a scroll-shaped note', price: 80, category: 'consumables', icon: '🍬', availability: 'limited', rarity: 'common' },
  { id: 'chocolate-coin-pack', name: 'Chocolate Coin Pack', description: '3 chocolate coins', price: 100, category: 'consumables', icon: '🪙', availability: 'weekly', rarity: 'rare' },
  { id: 'secret-fortune-cookie', name: 'Secret Fortune Cookie', description: 'Includes a fun XP challenge inside', price: 90, category: 'consumables', icon: '🥠', availability: 'weekly', rarity: 'common' },
  { id: 'super-slurp-juice-box', name: 'Super Slurp Juice Box', description: 'Natural fruit juice + XP joke on wrapper', price: 120, category: 'consumables', icon: '🧃', availability: 'limited', rarity: 'rare' },
  { id: 'golden-apple', name: 'Golden Apple', description: 'Fancy apple + wisdom quote inside', price: 90, category: 'consumables', icon: '🍏', availability: 'limited', rarity: 'rare' },
  { id: 'ice-cream-pass', name: 'Ice Cream Pass', description: 'Redeem for 1 scoop from the TISA freezer', price: 200, category: 'consumables', icon: '🍦', availability: 'monthly', rarity: 'epic' },
  { id: 'sparkle-cookie', name: 'Sparkle Cookie', description: 'Decorated cookie (vegan option)', price: 150, category: 'consumables', icon: '🍪', availability: 'always', rarity: 'rare' },
  { id: 'mint-mood-booster', name: 'Mint Mood Booster', description: 'Breath mint + motivational quote', price: 50, category: 'consumables', icon: '🌿', availability: 'always', rarity: 'common' }
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get guild by ID
 */
export const getGuildById = (guildId: string): Guild | undefined => {
  return GUILDS.find(g => g.id === guildId);
};

/**
 * Get badge by ID
 */
export const getBadgeById = (badgeId: string): Badge | undefined => {
  return BADGES.find(b => b.id === badgeId);
};

/**
 * Get bank account by type
 */
export const getBankAccountByType = (accountType: string): BankAccount | undefined => {
  return BANK_ACCOUNTS.find(a => a.type === accountType);
};

/**
 * Get shop item by ID
 */
export const getShopItemById = (itemId: string): ShopItem | undefined => {
  return SHOP_ITEMS.find(i => i.id === itemId);
};

/**
 * Calculate interest for a given amount and account type
 */
export const calculateInterest = (amount: number, accountType: string): number => {
  const account = getBankAccountByType(accountType);
  if (!account) return 0;
  return Math.floor(amount * account.rate);
};

export default {
  GUILDS,
  BADGES,
  BANK_ACCOUNTS,
  CLASSES,
  LEVEL_PERKS,
  TEACHER_OVERRIDES,
  SHOP_ITEMS,
  XP_TO_LEVEL,
  LEVEL_TO_XP,
  getNextLevelPerks,
  hasSuperAccess,
  getGuildById,
  getBadgeById,
  getBankAccountByType,
  getShopItemById,
  calculateInterest
};
